import {
  CheckOutlined,
  CloseCircleOutlined,
  CloseOutlined,
  DeleteTwoTone,
  EditTwoTone,
  PlusOutlined,
} from '@ant-design/icons';
import { useMutation, useQuery } from '@apollo/client';
import {
  CREATE_METRIC,
  DELETE_METRIC,
  LIST_METRIC_UNITS,
  LIST_METRICS,
  MetricItem,
  MetricUnit,
  MetricUnitType,
  UPDATE_METRIC,
} from '@frontend/data-access';
import { App, Button, Form, Input, Select, Table } from 'antd';
import useTranslation from 'next-translate/useTranslation';
import React, { useState } from 'react';
import { TabHeader } from '../components';

interface EditableCellProps extends React.HTMLAttributes<HTMLElement> {
  editing: boolean;
  dataIndex: string;
  title: string;
  record: MetricItem;
  children: React.ReactNode;
}

type GroupedMetricUnits = {
  Qualitative: MetricUnit[];
  Quantitative: MetricUnit[];
};

export function MetricList() {
  const { notification } = App.useApp();
  const { t } = useTranslation('common');
  const [form] = Form.useForm();
  const [metricData, setMetricData] = useState<MetricItem[]>([]);
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const { data: defaultMetricData, loading } = useQuery<{
    listMetrics: MetricItem[];
  }>(LIST_METRICS, {
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      setMetricData(data.listMetrics);
    },
  });

  const { data: unitsData } = useQuery<{ listMetricUnits: MetricUnit[] }>(
    LIST_METRIC_UNITS
  );

  const handleDuplicationError = (error: any) => {
    if (error?.message?.toLowerCase().includes('conflict')) {
      setMetricData((prevData) => prevData.slice(0, -1));

      notification.error({
        message: t('metric_duplicated_error'),
        icon: (
          <span className="text-red-600">
            <CloseCircleOutlined />
          </span>
        ),
      });
    }
  };

  const [createMetric, { loading: createMetricLoading }] = useMutation(
    CREATE_METRIC,
    {
      refetchQueries: [{ query: LIST_METRICS }],
      onError: handleDuplicationError,
    }
  );

  const [updateMetric, { loading: updateMetricLoading }] = useMutation(
    UPDATE_METRIC,
    { refetchQueries: [{ query: LIST_METRICS }] }
  );
  const handleDeleteError = () => {
    notification.error({
      message: t('metric_used_error'),
      icon: (
        <span className="text-red-600">
          <CloseCircleOutlined />
        </span>
      ),
    });
  };

  const [deleteMetric, { loading: deleteMetricLoading }] = useMutation(
    DELETE_METRIC,
    {
      refetchQueries: [{ query: LIST_METRICS }],
      onError: handleDeleteError,
    }
  );

  const handleAddMetric = () => {
    const newKey = Date.now().toString();
    const newRow: MetricItem = {
      id: newKey,
      name: '',
      description: '',
      unit: undefined,
    };

    setMetricData([...(defaultMetricData?.listMetrics ?? []), newRow]);
    setEditingKey(newKey);

    form.setFieldsValue({
      name: '',
      description: '',
      unit: undefined,
    });
  };

  const handleDeleteMetric = (id: string) => {
    deleteMetric({ variables: { id } });
    setDeleteId(id);
  };

  const isEditing = (record: MetricItem) => record.id === editingKey;

  const edit = (record: MetricItem) => {
    form.setFieldsValue({
      name: record.name,
      unit: record.unit?.id,
      description: record.description,
    });
    setEditingKey(record.id);
  };

  const cancel = () => {
    const currentEditingRow = metricData.find((item) => item.id === editingKey);
    if (currentEditingRow && !currentEditingRow.name) {
      setMetricData(metricData.filter((item) => item.id !== editingKey));
    }
    setEditingKey(null);
  };

  const save = async (key: string) => {
    const row = await form.validateFields();
    const index = metricData.findIndex((item) => key === item.id);

    if (index > -1) {
      if (key.length !== Date.now().toString().length) {
        // Update
        await updateMetric({
          variables: {
            input: {
              id: key,
              name: row.name,
              description: row.description,
              unitId: row.unit,
            },
          },
        });
      } else {
        // Create
        await createMetric({
          variables: {
            input: {
              name: row.name,
              description: row.description,
              unitId: row.unit,
            },
          },
        });
      }
    }
    setEditingKey(null);
  };

  const getGroupedOptions = () => {
    if (!unitsData?.listMetricUnits) return [];

    const grouped = unitsData.listMetricUnits.reduce(
      (acc: GroupedMetricUnits, unit) => {
        const category =
          unit?.type === MetricUnitType.QUALITATIVE
            ? 'Qualitative'
            : 'Quantitative';
        if (!acc[category]) {
          acc[category] = [];
        }
        acc[category].push(unit);
        return acc;
      },
      { Qualitative: [], Quantitative: [] }
    );

    return [
      {
        label: t('qualitative'),
        options: grouped.Qualitative.map((unit) => ({
          label: unit?.label,
          value: unit?.id,
        })),
      },
      {
        label: t('quantitative'),
        options: grouped.Quantitative.map((unit) => ({
          label: unit?.label,
          value: unit?.id,
        })),
      },
    ];
  };

  const columns = [
    {
      title: t('metric'),
      dataIndex: 'name',
      key: 'name',
      editable: true,
      render: (_: unknown, record: MetricItem) => {
        const editable = isEditing(record);
        return editable ? (
          <Form.Item
            name="name"
            className="!m-0"
            rules={[{ required: true, message: t('metric_name_required') }]}
          >
            <Input data-test="metric-name" placeholder={t('enter_name')} />
          </Form.Item>
        ) : (
          <span>{record.name}</span>
        );
      },
    },
    {
      title: t('metric_description'),
      dataIndex: 'description',
      key: 'description',
      editable: true,
      width: 250,
      render: (_: unknown, record: MetricItem) => {
        const editable = isEditing(record);
        return editable ? (
          <Form.Item
            name="description"
            className="!m-0"
            rules={[{ required: true, message: t('metric_desc_required') }]}
          >
            <Input.TextArea
              rows={1}
              maxLength={150}
              data-test="metric-description"
              placeholder={t('metric_desc_required')}
            />
          </Form.Item>
        ) : (
          <span className="line-clamp-4 max-w-[250px]">
            {record.description || '-'}
          </span>
        );
      },
    },
    {
      title: t('measuring_unit'),
      dataIndex: 'unit',
      key: 'unit',
      editable: true,
      width: 120,
      render: (_: unknown, record: MetricItem) => {
        const editable = isEditing(record);
        return editable ? (
          <Form.Item
            name="unit"
            className="!m-0"
            rules={[{ required: true, message: t('select_unit_required') }]}
          >
            <Select
              data-test="metric-unit"
              placeholder={t('select_unit')}
              options={getGroupedOptions()}
            />
          </Form.Item>
        ) : (
          <span>{record.unit?.label}</span>
        );
      },
    },
    {
      title: '',
      key: 'actions',
      width: 80,
      render: (_: unknown, record: MetricItem) => {
        const editable = isEditing(record);
        return editable ? (
          <span className="flex flex-row">
            <Button
              type="link"
              onClick={() => save(record.id)}
              loading={createMetricLoading || updateMetricLoading}
              data-test="metric-save-btn"
            >
              <CheckOutlined />
            </Button>
            <Button type="link" onClick={cancel}>
              <CloseOutlined />
            </Button>
          </span>
        ) : (
          <div className="flex gap-2">
            <Button
              type="text"
              disabled={editingKey !== null}
              onClick={() => edit(record)}
              icon={<EditTwoTone twoToneColor="#0473EA" />}
              data-test="metric-edit-btn"
            />
            <Button
              type="text"
              icon={<DeleteTwoTone twoToneColor="#FF4D4F" />}
              onClick={() => handleDeleteMetric(record.id)}
              loading={deleteMetricLoading && deleteId === record.id}
              data-test="metric-delete-btn"
            />
          </div>
        );
      },
    },
  ];

  const EditableCell: React.FC<EditableCellProps> = ({
    editing,
    dataIndex,
    title,
    children,
    ...restProps
  }) => {
    return <td {...restProps}>{children}</td>;
  };

  return (
    <div className="space-y-6">
      <TabHeader
        title={t('metric_management')}
        description={t('create_metrics_description')}
      />
      <Form form={form} component={false}>
        <Table
          bordered
          rowKey="id"
          size="small"
          components={{
            body: {
              cell: EditableCell,
            },
          }}
          loading={loading}
          dataSource={metricData}
          columns={columns}
          pagination={false}
          rowClassName="editable-row"
        />
      </Form>

      <Button
        type="default"
        shape="round"
        icon={<PlusOutlined />}
        onClick={handleAddMetric}
        className="mt-4"
        data-test="add-metric-btn"
      >
        {t('add_metric')}
      </Button>
    </div>
  );
}
